import type { FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  Entitlements,
  PremiumFeatures,
  useWorkspaceHasPremiumFeature,
} from '@trello/entitlements';
import { useFeatureGate } from '@trello/feature-gate-client';
import { BoardIdProvider, useWorkspaceId } from '@trello/id-context';
import { Button } from '@trello/nachos/button';

import { BoardTileLargeSkeleton } from 'app/src/components/BoardTile/BoardTileLargeSkeleton';
import { CreateBoardTileLarge } from 'app/src/components/BoardTile/CreateBoardTileLarge';
import { PrivateBoardTileLarge } from 'app/src/components/BoardTile/PrivateBoardTileLarge';
import { ClosedBoardsButtonLazyWrapped as ClosedBoardsButton } from 'app/src/components/ClosedBoardsButton';
import { useWorkspaceBoardsFilteredQuery } from './useWorkspaceBoardsFilteredQuery';
import { useWorkspaceBoardLimitsFragment } from './WorkspaceBoardLimitsFragment.generated';
import { WorkspaceBoardsListItem } from './WorkspaceBoardsListItem';

import styles from './WorkspaceBoardsListSection.less';

interface WorkspaceBoardsListSectionProps {
  orgId: string;
  isWorkspaceMember: boolean;
}

export const WorkspaceBoardsListSection: FunctionComponent<
  WorkspaceBoardsListSectionProps
> = ({ orgId, isWorkspaceMember }) => {
  const workspaceId = useWorkspaceId() ?? '';

  const { data: organization } = useWorkspaceBoardLimitsFragment({
    from: { id: orgId },
  });

  const {
    boards,
    isLoading,
    fetchNextPage,
    refetchCurrentQuery,
    allBoardsFetched,
  } = useWorkspaceBoardsFilteredQuery({
    orgId,
  });

  /**
   * `freeBoardsPerOrg.count` only exists for free orgs (where we limit the number
   * of boards to 10) and is only populated once they have created at least 3 boards.
   * Using this value to derive the number of private boards the user doesn't have
   * access to is pretty hacky, but it's how this view has worked for the last few
   * years, so gonna keep it that way for now.
   */
  let privateTileCount = 0;
  if (
    !isLoading &&
    isWorkspaceMember &&
    Entitlements.isFree(organization?.offering) &&
    organization?.limits?.orgs?.freeBoardsPerOrg?.count &&
    Number.isInteger(organization?.limits?.orgs?.freeBoardsPerOrg?.count)
  ) {
    privateTileCount =
      organization.limits.orgs.freeBoardsPerOrg.count - boards.length;
  }

  const { value: isRedesignEnabled } = useFeatureGate(
    'trello_personal_productivity_release',
  );

  const isTaggingEnabled = useWorkspaceHasPremiumFeature(
    workspaceId,
    PremiumFeatures.tags,
  );

  // If tagging is enabled with the redesigned tiles, we want the list items to render using
  // subgrids (tiles and tags in separate rows) to ensure the tiles are the same height
  const useSubgrid = isRedesignEnabled && isTaggingEnabled;

  return (
    <>
      <ul className={styles.boardTileList}>
        {isWorkspaceMember && (
          <li className={useSubgrid ? styles.listItem : undefined}>
            <CreateBoardTileLarge
              analyticsSource="workspaceBoardsScreen"
              className={isRedesignEnabled ? styles.newCreateTile : undefined}
            />
          </li>
        )}
        {isLoading &&
          Array.from({ length: 7 }, (_, index) => index).map((index) => (
            <li
              key={`skeleton-${index}`}
              className={useSubgrid ? styles.listItem : undefined}
            >
              <BoardTileLargeSkeleton />
            </li>
          ))}
        {boards.map((board) => (
          <BoardIdProvider key={board.id} value={board.id}>
            <li className={useSubgrid ? styles.listItem : undefined}>
              <WorkspaceBoardsListItem />
            </li>
          </BoardIdProvider>
        ))}
        {Array.from({ length: privateTileCount }, (_, index) => index).map(
          (index) => (
            <li
              key={`private-${index}`}
              className={useSubgrid ? styles.listItem : undefined}
            >
              <PrivateBoardTileLarge />
            </li>
          ),
        )}
      </ul>

      {!allBoardsFetched && (
        <footer className={styles.loadMoreFooter}>
          <Button onClick={fetchNextPage} isDisabled={isLoading}>
            <FormattedMessage
              id="templates.workspace_page.load-more-boards"
              defaultMessage="Load more boards"
              description="Load more boards"
            />
          </Button>
        </footer>
      )}

      {isWorkspaceMember && (
        <footer className={styles.closedBoardsFooter}>
          <ClosedBoardsButton
            orgId={orgId}
            analyticsSource="workspaceBoardsScreen"
            onBoardReopened={refetchCurrentQuery}
          />
        </footer>
      )}
    </>
  );
};
